// src/components/sourcex/StepProgressOverlay.jsx
//
// SourceX — the "this is going to take a few minutes" screen.
//
// Steps 1, 2 and 3 all run something slow: a protocol extraction, an eCRF
// extraction that can span several server passes, a mapping that calls a model.
// Each used to show a spinner and a line of text somewhere on the page, which
// left a coordinator watching a page that looked broken, with no idea whether
// to wait, whether it was safe to leave, or what to do if it stopped.
//
// So it takes the whole screen and answers exactly those three questions:
//   what is happening · roughly how long · what you may safely do meanwhile
//
// TWO THINGS IT IS CAREFUL ABOUT
//
// 1. It never invents a countdown. Where a step reports real progress (the eCRF
//    extraction reports chunks) the bar is real and the estimate is measured
//    from this run's own pace. Where it does not, the bar says so by being
//    indeterminate, and the only number shown is the time actually elapsed.
//
// 2. "Keep this tab open" is a per-step FACT, not a blanket warning. The eCRF
//    extraction's resume loop and the mapping's save both run in this tab, so
//    closing it really does lose the run. A protocol extraction is fire and
//    forget on the server and survives a closed tab. Telling somebody they must
//    stay when they need not trains them to ignore the one time it matters.
//
// Presentation only: it starts nothing, retries nothing and writes nothing. It
// renders what it is handed and calls back.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  Coffee,
  RefreshCw,
  Wifi,
  AppWindow,
  Minimize2,
  X,
} from "lucide-react";

/** "4:07" — elapsed time, counted in this tab from the moment work started. */
const clock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/** "about 3 min left", or "" when there is nothing honest to say yet. */
const remaining = (fraction, elapsedMs) => {
  // Below a few percent the extrapolation is noise, and a wrong number is worse
  // than no number — somebody who is told "1 min" and waits eight stops
  // believing the screen.
  if (!fraction || fraction < 0.05 || elapsedMs < 8000) return "";
  const totalMs = elapsedMs / fraction;
  const leftMin = Math.ceil((totalMs - elapsedMs) / 60000);
  if (leftMin <= 1) return "less than a minute left";
  return `about ${leftMin} min left`;
};

const Rule = ({ icon: Icon, children, strong }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm ${
      strong
        ? "border-amber-400/40 bg-amber-400/10 text-amber-100"
        : "border-white/12 bg-white/[0.04] text-slate-300"
    }`}
  >
    <Icon className="w-4 h-4 shrink-0" />
    {children}
  </span>
);

/**
 * @param {boolean} open
 * @param {string} title            what is happening — "Reading the eCRF"
 * @param {string} [detail]         one line under it, from the step itself
 * @param {number|null} [value]     0..1 for a real bar; null/undefined = unknown
 * @param {string} [typical]        "usually 2–5 min", shown until a real
 *                                  estimate can be measured from this run
 * @param {boolean} [keepTabOpen]   closing this tab really does lose the run
 * @param {string} [error]          switches to the stopped state
 * @param {() => void} [onRetry]    offered in the stopped state
 * @param {string} [retryLabel]     "Resume" / "Retry" / "Try again"
 * @param {() => void} [onDismiss]  close the stopped state
 */
export default function StepProgressOverlay({
  open,
  title,
  detail,
  value = null,
  typical = "",
  keepTabOpen = false,
  error = "",
  onRetry,
  retryLabel = "Retry",
  onDismiss,
}) {
  const [elapsed, setElapsed] = useState(0);
  // Minimised to a corner pill. The work does not stop — this is for somebody
  // who took the advice, went and did something else, and wants the page back.
  const [hidden, setHidden] = useState(false);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!open) {
      setHidden(false);
      setElapsed(0);
      return undefined;
    }
    startedAt.current = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed(Date.now() - startedAt.current), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Leaving mid-run costs the run, so the browser asks first. Only while that
  // is actually true — see the note at the top of this file — and only while
  // work is in flight, so it can never strand somebody on a page that will not
  // let them leave.
  useEffect(() => {
    if (!open || !keepTabOpen || error) return undefined;
    const warn = (e) => {
      e.preventDefault();
      // Chrome ignores the string and shows its own wording; it needs the
      // assignment anyway to treat the handler as a real prompt.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open, keepTabOpen, error]);

  if (!open) return null;

  const pct = typeof value === "number" ? Math.round(Math.min(1, Math.max(0, value)) * 100) : null;
  const eta = remaining(value, elapsed);

  if (hidden && !error) {
    return createPortal(
      <motion.button
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => setHidden(false)}
        // regx-progress-pill: this is portalled to document.body, so the
        // .sourcex-page-scoped light-mode override never reaches it. Without a
        // hook of its own the card stayed dark in light mode while the global
        // text rules turned its label near-black — unreadable. See index.css.
        className="regx-progress-pill fixed bottom-4 right-4 z-[95] inline-flex items-center gap-2.5 rounded-2xl border border-white/15 bg-[#0c0726]/95 px-4 py-3 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.9)] backdrop-blur-xl hover:bg-[#141034]/95 transition"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </span>
        <span className="text-left">
          <span className="block text-sm font-semibold text-white">{title}</span>
          <span className="block text-xs text-slate-400 tabular-nums">
            {pct !== null ? `${pct}%` : clock(elapsed)}
          </span>
        </span>
      </motion.button>,
      document.body
    );
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-modal="true"
        aria-live="polite"
        className="fixed inset-0 z-[95] flex items-center justify-center bg-[#07041a]/92 backdrop-blur-md p-6"
      >
        <motion.div
          initial={{ y: 10, scale: 0.99 }}
          animate={{ y: 0, scale: 1 }}
          className="w-full max-w-xl text-center"
        >
          {error ? (
            // Stopped. Says so plainly and offers the one thing worth doing —
            // picking up where it left off. An eCRF keeps its finished chunks,
            // so resuming really does continue rather than start again.
            <>
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-rose-400/40 bg-rose-500/15">
                <AlertTriangle className="h-7 w-7 text-rose-300" />
              </span>
              <h2 className="mt-4 text-2xl font-bold text-white">Stopped.</h2>
              <p className="mx-auto mt-2 max-w-lg text-base text-rose-200/90">{error}</p>
              <p className="mt-1 text-sm text-slate-500">
                Nothing already finished is lost.
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 text-base font-semibold text-white transition hover:brightness-110"
                  >
                    <RefreshCw className="h-4 w-4" /> {retryLabel}
                  </button>
                )}
                {onDismiss && (
                  <button
                    onClick={onDismiss}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/12 bg-white/[0.05] px-5 py-3 text-base text-slate-300 transition hover:bg-white/[0.1] hover:text-white"
                  >
                    <X className="h-4 w-4" /> Close
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white">{title}</h2>
              {detail && <p className="mt-1.5 text-base text-slate-400">{detail}</p>}

              {/* The bar. Determinate when the step reports real progress;
                  otherwise a sweep that says "working" without pretending to
                  know how far along it is. */}
              <div className="relative mt-6 h-2.5 w-full overflow-hidden rounded-full bg-white/[0.07]">
                {pct !== null ? (
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400"
                    initial={false}
                    animate={{ width: `${pct}%` }}
                    transition={{ ease: "easeOut", duration: 0.5 }}
                  />
                ) : (
                  <motion.div
                    className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
                    animate={{ left: ["-33%", "100%"] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
                  />
                )}
              </div>

              <div className="mt-2.5 flex items-center justify-between text-sm tabular-nums text-slate-500">
                <span>{clock(elapsed)}</span>
                <span>{eta || typical}</span>
              </div>

              {/* What you may safely do. Three chips, no paragraph. */}
              <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
                <Rule icon={Coffee}>Go do something else</Rule>
                {keepTabOpen && (
                  <Rule icon={AppWindow} strong>
                    Keep this tab open
                  </Rule>
                )}
                <Rule icon={Wifi}>Stay connected</Rule>
              </div>

              <button
                onClick={() => setHidden(true)}
                className="mx-auto mt-7 inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-200"
              >
                <Minimize2 className="h-4 w-4" /> Hide
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
